import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, alpha } from '@mui/material';
import {
  Edit as EditIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Person as PersonIcon,
  Star as StarIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Notes as NotesIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Archive as ArchiveIcon,
  HourglassEmpty as HourglassIcon,
  TrendingUp as TrendingUpIcon,
  Article as ArticleIcon,
  Circle as CircleIcon,
  AccessTime as TimeIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
import { format, differenceInDays } from 'date-fns';
import Loading from '../../common/Loading';
import Header from '../../common/Header';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canAccessPartnershipManagement } from '../../../utils/partnershipPermissions';
import { resolveUserId, userDetailsPath } from '../../../utils/userDetailsLink';
import { toStorageUrl } from '../../../utils/storageUrl';
import { fetchPartnerDocumentPreviewBlob, openBlobUrlInNewTab } from '../../../utils/protectedFilePreview';
import SystemInformationSection from '../../common/SystemInformationSection';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';

// ─── Design tokens ────────────────────────────────────────────────────────────
const NEON    = '#38BDF8';
const NEON2   = '#818CF8';
const NEON3   = '#34D399';
const AMBER   = '#FBBF24';
const CORAL   = '#F87171';
const SURFACE = '#060f1e';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const SURFACE_TOP = '#152640';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';
const TEXT_SUB    = '#94A3B8';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const webHref = (u) => {
  if (!u) return null;
  const s = String(u).trim();
  if (!s) return null;
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

const PARTNER_DOCUMENTS = [
  { key: 'signed_agreement_path', docType: 'signed_agreement', label: 'Signed Agreement', icon: '📄' },
  { key: 'receipt_utm_path',       docType: 'receipt_utm',      label: 'Receipt (UTM)',     icon: '🧾' },
  { key: 'receipt_company_path',   docType: 'receipt_company',  label: 'Receipt (Company)', icon: '🧾' },
  { key: 'invoice_path',           docType: 'invoice',          label: 'Invoice',           icon: '📋' },
  { key: 'other_document_path',    docType: 'other_document',   label: 'Other Document',    icon: '📎' },
];

const getStatusConfig = (status) => {
  const map = {
    active:   { color: NEON3,   icon: CheckCircleIcon, label: 'Active',   bg: alpha('#34D399', 0.12) },
    inactive: { color: CORAL,   icon: ArchiveIcon,     label: 'Archived', bg: alpha('#F87171', 0.12) },
    pending:  { color: AMBER,   icon: HourglassIcon,   label: 'Pending',  bg: alpha('#FBBF24', 0.12) },
  };
  return map[status?.toLowerCase()] ?? { color: TEXT_SUB, icon: HourglassIcon, label: status, bg: alpha(TEXT_SUB, 0.1) };
};

// ─── HERO BANNER ─────────────────────────────────────────────────────────────
const HeroBanner = ({ partner, daysRemaining, partnershipDuration }) => {
  const statusCfg = getStatusConfig(partner.status);
  const StatusIcon = statusCfg.icon;
  const amountFormatted = `RM ${parseFloat(partner.sponsor_amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Box sx={{
      position: 'relative',
      borderRadius: '20px',
      overflow: 'hidden',
      border: `1px solid ${alpha(NEON2, 0.25)}`,
      background: `linear-gradient(135deg, ${alpha('#1a0a40', 0.95)} 0%, ${alpha(SURFACE_MID, 0.98)} 60%, ${alpha('#0d1f3c', 0.95)} 100%)`,
      animation: 'heroIn 0.5s cubic-bezier(.22,1,.36,1) both',
      '@keyframes heroIn': { from: { opacity: 0, transform: 'translateY(-10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
    }}>
      {/* Decorative mesh */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(ellipse 60% 50% at 80% 20%, ${alpha(NEON2, 0.08)} 0%, transparent 70%),
                     radial-gradient(ellipse 40% 60% at 10% 80%, ${alpha(NEON, 0.05)} 0%, transparent 70%)`,
      }} />

      {/* Subtle grid lines */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
        backgroundImage: `linear-gradient(${alpha(NEON2, 0.8)} 1px, transparent 1px),
                          linear-gradient(90deg, ${alpha(NEON2, 0.8)} 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }} />

      <Box sx={{ position: 'relative', p: { xs: 2.5, md: 3.5 } }}>
        {/* Top row: company identity + status */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Logo placeholder */}
            <Box sx={{
              width: 56, height: 56, borderRadius: '14px', flexShrink: 0,
              background: `linear-gradient(135deg, ${alpha(NEON2, 0.3)}, ${alpha(NEON, 0.15)})`,
              border: `1.5px solid ${alpha(NEON2, 0.4)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 0 20px ${alpha(NEON2, 0.15)}`,
            }}>
              <BusinessIcon sx={{ fontSize: 26, color: NEON2 }} />
            </Box>

            <Box>
              <Typography sx={{
                fontSize: { xs: '1.5rem', md: '1.9rem' },
                fontWeight: 800, fontFamily: "'Rajdhani', sans-serif",
                color: TEXT_BODY, letterSpacing: 0.5, lineHeight: 1.1,
              }}>
                {partner.company?.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
                <StarIcon sx={{ fontSize: 13, color: AMBER }} />
                <Typography sx={{ fontSize: '0.8rem', color: TEXT_SUB, fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  {partner.sponsor_tier?.tier_name || 'Standard'} Tier Partner
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Status badge */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.8,
            px: 1.8, py: 0.7, borderRadius: '30px',
            background: statusCfg.bg,
            border: `1.5px solid ${alpha(statusCfg.color, 0.4)}`,
            backdropFilter: 'blur(8px)',
          }}>
            <CircleIcon sx={{ fontSize: 8, color: statusCfg.color }} />
            <Typography sx={{
              fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700, color: statusCfg.color, letterSpacing: '0.06em',
            }}>
              {statusCfg.label}
            </Typography>
          </Box>
        </Box>

        {/* ── KEY METRICS ROW ── */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: '2fr 1fr 1fr 1fr' },
          gap: 1.5,
        }}>
          {/* Sponsor Amount — most important, larger */}
          <Box sx={{
            gridColumn: { xs: '1 / -1', md: 'span 2' },
            borderRadius: '14px',
            p: 2.5,
            background: `linear-gradient(135deg, ${alpha(NEON2, 0.15)} 0%, ${alpha(NEON2, 0.05)} 100%)`,
            border: `1px solid ${alpha(NEON2, 0.3)}`,
            position: 'relative', overflow: 'hidden',
          }}>
            <Box sx={{
              position: 'absolute', top: -30, right: -20, width: 120, height: 120,
              borderRadius: '50%', background: alpha(NEON2, 0.07), filter: 'blur(30px)',
            }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
              <MoneyIcon sx={{ fontSize: 13, color: alpha(NEON2, 0.7) }} />
              <Typography sx={{ fontSize: '0.68rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>
                Sponsor Amount
              </Typography>
            </Box>
            <Typography sx={{
              fontSize: { xs: '2rem', md: '2.4rem' },
              fontWeight: 800, fontFamily: "'Rajdhani', sans-serif",
              color: NEON2, letterSpacing: 1, lineHeight: 1,
            }}>
              {amountFormatted}
            </Typography>
          </Box>

          {/* Tier */}
          <StatChip
            label="Tier"
            value={partner.sponsor_tier?.tier_name || '—'}
            icon={StarIcon}
            color={AMBER}
          />

          {/* Days remaining */}
          {daysRemaining !== null ? (
            <StatChip
              label={daysRemaining > 0 ? 'Days Left' : 'Expired'}
              value={daysRemaining > 0 ? daysRemaining : '—'}
              sub={daysRemaining > 0 ? `of ${partnershipDuration}d` : 'Partnership ended'}
              icon={daysRemaining > 0 ? TrendingUpIcon : ArchiveIcon}
              color={daysRemaining > 30 ? NEON3 : daysRemaining > 0 ? AMBER : CORAL}
            />
          ) : (
            <StatChip label="Duration" value="—" icon={TimeIcon} color={TEXT_SUB} />
          )}
        </Box>

        {/* Progress bar + dates (if dates exist) */}
        {partnershipDuration != null && daysRemaining != null && (
          <Box sx={{ mt: 2 }}>
            {/* Label row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
              <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Partnership Progress
              </Typography>
              <Typography sx={{
                fontSize: '0.7rem', fontFamily: "'DM Sans', sans-serif",
                color: daysRemaining > 0 ? NEON3 : CORAL, fontWeight: 700, letterSpacing: '0.05em',
              }}>
                {daysRemaining > 0
                  ? `${Math.round(((partnershipDuration - daysRemaining) / partnershipDuration) * 100)}% elapsed`
                  : 'Completed'}
              </Typography>
            </Box>

            {/* Bar */}
            <Box sx={{ height: 5, borderRadius: 99, background: alpha('#fff', 0.07), overflow: 'hidden' }}>
              <Box sx={{
                height: '100%',
                width: `${Math.min(100, Math.max(0, ((partnershipDuration - daysRemaining) / partnershipDuration) * 100))}%`,
                borderRadius: 99,
                background: daysRemaining > 0 ? `linear-gradient(90deg, ${NEON2}, ${NEON})` : CORAL,
                transition: 'width 0.8s cubic-bezier(.22,1,.36,1)',
                boxShadow: daysRemaining > 0 ? `0 0 10px ${alpha(NEON, 0.5)}` : 'none',
              }} />
            </Box>

            {/* Start / End date labels below bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <CalendarIcon sx={{ fontSize: 11, color: alpha(NEON2, 0.6) }} />
                <Typography sx={{ fontSize: '0.68rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED }}>
                  <Box component="span" sx={{ color: TEXT_SUB, fontWeight: 600, mr: 0.5 }}>Start</Box>
                  {partner.sponsor_start_date ? format(new Date(partner.sponsor_start_date), 'dd MMM yyyy') : '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Typography sx={{ fontSize: '0.68rem', fontFamily: "'DM Sans', sans-serif", color: daysRemaining < 0 ? CORAL : TEXT_MUTED, textAlign: 'right' }}>
                  <Box component="span" sx={{ color: daysRemaining < 0 ? CORAL : TEXT_SUB, fontWeight: 600, mr: 0.5 }}>End</Box>
                  {partner.sponsor_end_date ? format(new Date(partner.sponsor_end_date), 'dd MMM yyyy') : '—'}
                </Typography>
                <CalendarIcon sx={{ fontSize: 11, color: daysRemaining < 0 ? alpha(CORAL, 0.6) : alpha(NEON2, 0.6) }} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Small stat chip in the hero */
const StatChip = ({ label, value, sub, icon: Icon, color }) => (
  <Box sx={{
    borderRadius: '14px', p: 2,
    background: alpha(color, 0.08),
    border: `1px solid ${alpha(color, 0.2)}`,
    display: 'flex', flexDirection: 'column', gap: 0.3,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
      <Icon sx={{ fontSize: 12, color: alpha(color, 0.75) }} />
      <Typography sx={{ fontSize: '0.65rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.68rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{sub}</Typography>}
  </Box>
);

// ─── SECTION CARD ─────────────────────────────────────────────────────────────
const SectionCard = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.92)} 100%)`,
    overflow: 'hidden',
    animation: `cardIn 0.45s cubic-bezier(.22,1,.36,1) ${delay}s both`,
    '@keyframes cardIn': { from: { opacity: 0, transform: 'translateY(14px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 15, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// ─── INFO ROW ─────────────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, href, onClick, color = NEON }) => {
  if (!value) return null;
  const isInteractive = Boolean(href || onClick);

  const inner = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.9, px: 1.2, borderRadius: '10px',
      transition: 'background 0.15s',
      cursor: isInteractive ? 'pointer' : 'default',
      '&:hover': isInteractive ? { background: alpha(color, 0.06) } : {},
    }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: '9px', flexShrink: 0,
        background: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.16)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 15, color: alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.66rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.9rem', color: isInteractive ? color : TEXT_BODY,
          fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {value}
        </Typography>
      </Box>
      {(href || onClick) && <OpenInNewIcon sx={{ fontSize: 12, color: alpha(color, 0.4), flexShrink: 0 }} />}
    </Box>
  );
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}>{inner}</a>;
  }
  if (onClick) {
    return (
      <Box
        role="link"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
          }
        }}
        sx={{ textDecoration: 'none', display: 'block' }}
      >
        {inner}
      </Box>
    );
  }
  return inner;
};

// ─── DOCUMENT ROW ─────────────────────────────────────────────────────────────
const DocRow = ({ label, icon, partnerId, docType, storedPath }) => {
  const handleOpen = async () => {
    try {
      const { url } = await fetchPartnerDocumentPreviewBlob(partnerId, docType, storedPath);
      openBlobUrlInNewTab(url);
    } catch {
      const fallback = toStorageUrl(storedPath);
      if (fallback) window.open(fallback, '_blank', 'noopener,noreferrer');
    }
  };

  return (
  <Box
    onClick={handleOpen}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      p: 1.5, borderRadius: '11px', cursor: 'pointer',
      border: `1px solid ${alpha(NEON, 0.12)}`,
      background: alpha(SURFACE_TOP, 0.5),
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: alpha(NEON, 0.35),
        background: alpha(NEON, 0.05),
        transform: 'translateX(4px)',
        boxShadow: `0 0 16px ${alpha(NEON, 0.08)}`,
      },
    }}
  >
    <Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>{icon}</Typography>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.87rem', color: TEXT_BODY }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: TEXT_MUTED }}>
        Click to open
      </Typography>
    </Box>
    <OpenInNewIcon sx={{ fontSize: 13, color: alpha(NEON, 0.4) }} />
  </Box>
  );
};

// ─── ACTION BUTTON ────────────────────────────────────────────────────────────
const ActionBtn = ({ icon: Icon, label, onClick, color, borderColor }) => (
  <Button
    startIcon={<Icon sx={{ fontSize: '15px !important' }} />}
    onClick={onClick}
    sx={{
      borderRadius: '10px', px: 2.2, py: 0.7,
      fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.83rem',
      background: 'transparent',
      border: `1.5px solid ${alpha(borderColor || color, 0.5)}`,
      color,
      transition: 'all 0.2s ease',
      '&:hover': {
        background: alpha(color, 0.08),
        borderColor: color,
        boxShadow: `0 0 16px ${alpha(color, 0.2)}`,
      },
    }}
  >
    {label}
  </Button>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const PartnerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const hasPartnershipAccess = canAccessPartnershipManagement(currentUser);

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!hasPartnershipAccess) { navigate('/dashboard'); return; }
    fetchPartner();
  }, [id, hasPartnershipAccess, navigate]);

  const fetchPartner = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/partners/${id}`);
      setPartner(res.data);
      setError(null);
    } catch {
      setError('Failed to load partner details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartner = async () => {
    try {
      const name = partner?.company?.name || 'Partner';
      const response = await api.request({ method: 'post', url: `/partners/${id}/archive` });
      if (response.data?.partner) {
        setPartner(response.data.partner);
      }
      setDeleteDialogOpen(false);
      setSuccessMessage(`Partnership with "${name}" archived successfully!`);
      setTimeout(() => navigate('/partners', { state: { refreshList: Date.now(), partnerStatusFilter: 'inactive' } }), 2000);
    } catch {
      setError('Failed to archive partner. Please try again.');
      setDeleteDialogOpen(false);
    }
  };

  if (loading) return <Loading message="Loading partner details" size={56} variant="primary" />;
  if (!partner && !loading) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message={error || 'Partner not found'} severity="error" />
    </Container>
  );

  const daysRemaining = partner?.sponsor_end_date
    ? differenceInDays(new Date(partner.sponsor_end_date), new Date())
    : null;
  const partnershipDuration = (partner?.sponsor_start_date && partner?.sponsor_end_date)
    ? differenceInDays(new Date(partner.sponsor_end_date), new Date(partner.sponsor_start_date))
    : null;

  const gmail = partner?.company?.gmail;
  const availableDocs = PARTNER_DOCUMENTS.filter(d => Boolean(partner?.[d.key]));
  const linkedCompanyId = partner?.company_id ?? partner?.company?.id ?? null;
  const canViewCompanyLink = canAccessPartnershipManagement(currentUser);
  const representative = partner?.company?.representative;
  const representativeId = resolveUserId(representative ?? partner?.company?.representative_user_id);

  const formatAuditUser = (record, field) => {
    if (field === 'created') return record?.creator?.name || record?.created_by_name || null;
    return record?.last_updater?.name || record?.updated_by_name || null;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>

      {/* Header */}
      <Header
        title="Partner Details"
        subtitle="View partner information"
        icon={HandshakeIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ActionBtn icon={EditIcon}   label="Edit"   onClick={() => navigate(`/partners/edit/${id}`)} color={NEON} />
            {!partner?.archived_at && (
              <ActionBtn icon={ArchiveIcon} label="Archive" onClick={() => setDeleteDialogOpen(true)} color={CORAL} />
            )}
          </Box>
        }
      />

      {/* Feedback */}
      {error && (
        <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
          sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
        />
      )}
      {successMessage && (
        <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)}
          sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha(NEON3, 0.08), border: `1px solid ${alpha(NEON3, 0.3)}`, color: NEON3 }}
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* ── 1. HERO ── */}
        <HeroBanner partner={partner} daysRemaining={daysRemaining} partnershipDuration={partnershipDuration} />

        {/* ── 2. Related Company + Assigned To + Benefits ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>

          <SectionCard title="Related Company" icon={BusinessIcon} delay={0.08}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
              <InfoRow
                icon={BusinessIcon}
                label="Company Name"
                value={partner.company?.name}
                color={NEON2}
                onClick={linkedCompanyId && canViewCompanyLink ? () => navigate(`/companies/${linkedCompanyId}`) : undefined}
              />
              <InfoRow icon={EmailIcon} label="Email" value={gmail} href={gmail ? `mailto:${gmail}` : null} color={NEON2} />
              <InfoRow icon={PhoneIcon} label="Contact Number" value={partner.company?.contact_number} color={NEON2} />
              {partner.company?.website_link && (
                <InfoRow
                  icon={WebsiteIcon}
                  label="Website"
                  value={partner.company.website_link.replace(/^https?:\/\//, '')}
                  href={webHref(partner.company.website_link)}
                  color={NEON2}
                />
              )}
            </Box>
          </SectionCard>

          <SectionCard title="Assigned To" icon={PersonIcon} accent={NEON3} delay={0.09}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
              <InfoRow
                icon={PersonIcon}
                label="Representative User"
                value={representative?.name || '—'}
                color={NEON3}
                onClick={representativeId ? () => navigate(userDetailsPath(representativeId)) : undefined}
              />
              {representative?.email && (
                <InfoRow icon={EmailIcon} label="Email" value={representative.email} href={`mailto:${representative.email}`} color={NEON3} />
              )}
            </Box>
          </SectionCard>
        </Box>

        <SectionCard title="Benefits & Notes" icon={StarIcon} accent={NEON} delay={0.1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Tier Benefits */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
                <StarIcon sx={{ fontSize: 13, color: alpha(NEON, 0.7) }} />
                <Typography sx={{
                  fontSize: '0.68rem', fontFamily: "'DM Sans', sans-serif",
                  color: TEXT_MUTED, textTransform: 'uppercase',
                  letterSpacing: '0.08em', fontWeight: 700,
                }}>
                  Tier Benefits
                </Typography>
              </Box>

              {partner.sponsor_tier?.benefit ? (
                <Box sx={{
                  px: 1.8, py: 1.4, borderRadius: '10px',
                  borderLeft: `3px solid ${NEON}`,
                  background: alpha(NEON, 0.08),
                }}>
                  <Typography sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '0.88rem', color: TEXT_BODY, lineHeight: 1.75,
                  }}>
                    {partner.sponsor_tier.benefit}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ opacity: 0.4, display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <StarIcon sx={{ fontSize: 13, color: TEXT_MUTED }} />
                  <Typography sx={{
                    fontSize: '0.82rem', fontFamily: "'DM Sans', sans-serif",
                    color: TEXT_MUTED, fontStyle: 'italic',
                  }}>
                    No tier benefits defined
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Divider — only when both exist */}
            {partner.sponsor_tier?.benefit && partner.notes && (
              <Box sx={{ height: 1, background: alpha(AMBER, 0.12), borderRadius: 99 }} />
            )}

            {/* Partnership Notes */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1 }}>
                <NotesIcon sx={{ fontSize: 13, color: alpha(NEON, 0.7) }} />
                <Typography sx={{
                  fontSize: '0.68rem', fontFamily: "'DM Sans', sans-serif",
                  color: TEXT_MUTED, textTransform: 'uppercase',
                  letterSpacing: '0.08em', fontWeight: 700,
                }}>
                  Partnership Notes
                </Typography>
              </Box>

              {partner.notes ? (
                <Box sx={{
                  px: 1.8, py: 1.4, borderRadius: '10px',
                  borderLeft: `3px solid ${alpha(NEON, 0.4)}`,
                  background: alpha(SURFACE_TOP, 0.45),
                }}>
                  <Typography sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '0.88rem', color: TEXT_BODY, lineHeight: 1.75,
                  }}>
                    {partner.notes}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ opacity: 0.4, display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <NotesIcon sx={{ fontSize: 13, color: TEXT_MUTED }} />
                  <Typography sx={{
                    fontSize: '0.82rem', fontFamily: "'DM Sans', sans-serif",
                    color: TEXT_MUTED, fontStyle: 'italic',
                  }}>
                    No notes added
                  </Typography>
                </Box>
              )}
            </Box>

          </Box>
        </SectionCard>

        {/* ── 3. Documents ── */}
        <SectionCard title="Partnership Documents" icon={ArticleIcon} delay={0.15}>
          {availableDocs.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.2 }}>
              {availableDocs.map(doc => (
                <DocRow
                  key={doc.key}
                  label={doc.label}
                  icon={doc.icon}
                  partnerId={partner.id}
                  docType={doc.docType}
                  storedPath={partner[doc.key]}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, opacity: 0.45 }}>
              <ArticleIcon sx={{ fontSize: 40, color: TEXT_MUTED, mb: 1.5 }} />
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, fontSize: '0.9rem', fontStyle: 'italic' }}>
                No documents uploaded yet
              </Typography>
            </Box>
          )}
        </SectionCard>

        {/* ── 4. System Info ── */}
        <SystemInformationSection
          createdBy={formatAuditUser(partner, 'created') || '—'}
          createdAt={partner.created_at ? format(new Date(partner.created_at), 'dd MMM yyyy, h:mm a') : '—'}
          updatedBy={formatAuditUser(partner, 'updated') || '—'}
          updatedAt={partner.updated_at ? format(new Date(partner.updated_at), 'dd MMM yyyy, h:mm a') : '—'}
          delay={0.22}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeletePartner}
        itemType="partnership"
        itemName={partner?.company?.name}
        title="Archive Partner"
        description="This will mark the partnership as inactive, hide it from public display, and keep its documents and history."
        confirmLabel="Archive Partner"
        actionVerb="archive"
      />
    </Container>
  );
};

export default PartnerDetails;
